/* Stash Shop – product page helpers (loaded by blocks/ss-breadcrumb.liquid).
   1. Sticky product details (desktop): Fabric sticks the details column at the top of the scroll area. When the column is
      taller than the window (long titles, many options, Bold Product Options / FastBundle content) its bottom, including
      Add to cart, could never be reached until the gallery ended. Here the sticky offset is set so the column scrolls
      until its bottom is in view, then sticks there; short columns stick just below the header as before.
   2. Sticky add-to-cart bar: Fabric hides its bar whenever the footer is within 200px of the window. On short product
      pages (especially mobile) that meant the main button had scrolled away while the bar stayed hidden, so Add to cart
      was nowhere on screen. Here the bar stays shown whenever the main button has scrolled (even partly) up out of view; ss-extra.css pads the
      page bottom while it is shown so it never covers the footer.
   3. Breadcrumb: when the page has no collection context, add the collection the shopper came from (same-site referrer).
   4. Sticky bar Add to cart presses the main Add to cart button instead of adding on its own, so apps hooked to the main
      button/form (Warnify warning popup + order-line note, Bold Product Options' own copy of the button) run from the
      sticky bar too.
   6. Add to bag opens Fabric's cart drawer (Cart type = Drawer, auto-open); the drawer has View Bag instead of checkout.
      The old redirect to /cart was removed on 9 Oct 2026. */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const GAP = 16;

  function headerOffset() {
    const header = document.querySelector('.hr-header');
    if (!header) return 0;
    const r = header.getBoundingClientRect();
    const pos = getComputedStyle(header.closest('.shopify-section') || header).position;
    return pos === 'sticky' || pos === 'fixed' ? Math.max(0, Math.round(r.height)) : 0;
  }

  function viewportHeight() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && getComputedStyle(wrapper).overflowY !== 'visible') return wrapper.clientHeight;
    return window.innerHeight;
  }

  function fitSticky() {
    document.querySelectorAll('.product-details.sticky-content--desktop').forEach((details) => {
      if (!DESKTOP.matches) {
        details.style.removeProperty('top');
        return;
      }
      const vh = viewportHeight();
      const top = headerOffset() + GAP;
      const h = details.offsetHeight;
      details.style.top = h + top + GAP <= vh ? `${top}px` : `${Math.round(vh - h - GAP * 2)}px`;
    });
  }

  let raf = 0;
  const schedule = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(fitSticky);
  };

  function initSticky() {
    fitSticky();
    window.addEventListener('resize', schedule, { passive: true });
    DESKTOP.addEventListener?.('change', schedule);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(schedule);
      document.querySelectorAll('.product-details.sticky-content--desktop').forEach((d) => ro.observe(d));
      const header = document.querySelector('.hr-header');
      if (header) ro.observe(header);
    }
    // App content (Bold, FastBundle) can arrive late.
    [500, 1500, 3000, 6000].forEach((t) => setTimeout(schedule, t));
  }

  function syncStickyBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    const main = mainAddButton(); // visible add button (Bold Product Options hides Fabric's and shows its .bold_clone)
    if (!bar || !main || !main.offsetParent) return;
    const r = main.getBoundingClientRect();
    const limit = headerOffset(); // anything above the sticky header's bottom edge is hidden or only partly visible
    if (r.top < limit) {
      if (bar.dataset.stuck !== 'true') bar.dataset.stuck = 'true';
    } else if (bar.dataset.stuck === 'true') {
      bar.dataset.stuck = 'false';
    }
  }

  function initStickyBar() {
    let pending = 0;
    const run = () => {
      if (pending) return;
      pending = requestAnimationFrame(() => {
        pending = 0;
        syncStickyBar();
      });
    };
    // Desktop scrolls inside .page-wrapper, mobile scrolls the window; capture catches both.
    document.addEventListener('scroll', run, { capture: true, passive: true });
    window.addEventListener('resize', run, { passive: true });
    // Fabric's own observers can hide the bar after the last scroll event; put it back if the main button is still away.
    const bar = document.querySelector('.sticky-add-to-cart__bar');
    if (bar && 'MutationObserver' in window) {
      new MutationObserver(run).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    }
    run();
  }

  function mainAddButton() {
    // Bold Product Options hides Fabric's button and shows its own copy (.bold_clone) that adds the option extras first.
    const buttons = document.querySelectorAll('.product-details form[action*="/cart/add"] button.bold_clone, .product-details form[action*="/cart/add"] button[type="submit"]');
    for (const b of buttons) if (b.offsetParent !== null && !b.closest('[data-ss-bulk-form]')) return b; // visible one (apps may hide the original and add a copy); never the bulk grid's Add all
    return document.querySelector('[id^="BuyButtons-ProductSubmitButton"]');
  }

  function initStickyForward() {
    // Window capture runs before Fabric's own document-level handlers, so the bar's add never runs alongside the main one.
    window.addEventListener(
      'click',
      (e) => {
        const sticky = e.target.closest?.('.sticky-add-to-cart__bar .add-to-cart-button, .sticky-add-to-cart__bar button[ref="addToCartButton"]');
        if (!sticky) return;
        const main = mainAddButton();
        if (!main || main === sticky || main.disabled || main.getAttribute('aria-disabled') === 'true') return; // fall back to Fabric's own behaviour
        e.preventDefault();
        e.stopImmediatePropagation();
        // Warnify watches mousedown on the main button; if it opens its popup it cancels this event and presses the
        // main button itself after OK. Otherwise add straight away.
        const proceed = main.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window }));
        if (proceed) main.click();
      },
      true
    );
  }

  async function initBreadcrumb() {
    const list = document.querySelector('.ss-breadcrumb__list');
    if (!list || list.querySelector('[data-ss-crumb-collection]')) return;
    let ref;
    try {
      ref = new URL(document.referrer);
    } catch (e) {
      return;
    }
    if (ref.origin !== location.origin) return;
    const m = ref.pathname.match(/^\/collections\/([^/]+)\/?$/);
    if (!m || m[1] === 'all' || m[1] === 'vendors' || m[1] === 'types') return;
    const handle = m[1];
    let title = '';
    try {
      const res = await fetch(`/collections/${handle}.json`, { headers: { Accept: 'application/json' } });
      if (res.ok) title = (await res.json())?.collection?.title || '';
    } catch (e) {
      /* ignore */
    }
    if (!title) return;
    const li = document.createElement('li');
    li.className = 'ss-breadcrumb__item';
    li.dataset.ssCrumbCollection = '';
    const a = document.createElement('a');
    a.href = `/collections/${handle}`;
    a.textContent = title;
    li.append(a);
    list.querySelector('[data-ss-crumb-fallback]')?.remove(); /* Stash 10 Oct 2026: shopper's own collection beats the Liquid fallback */
    const current = list.querySelector('.ss-breadcrumb__item--current');
    list.insertBefore(li, current || null);
  }

  /* 5. Size guide link -> MP Size Chart (Avada) app chart for this product.
     The app renders a hidden-by-us floating trigger (#Avada-SC-button) only when a chart matches the product;
     clicking our link clicks that trigger. No app chart (or app not loaded yet) -> normal link to /pages/sizeguide. */
  function sizeChartTrigger() {
    return document.querySelector('#Avada-SC-button [role="button"], #Avada-SC-button .float-wrapper');
  }
  function initSizeGuide() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest?.('.ss-size-guide__link');
      if (!link) return;
      const trigger = sizeChartTrigger();
      if (!trigger) return;
      e.preventDefault();
      trigger.click();
    });
    const mark = () => {
      const has = !!sizeChartTrigger();
      document.documentElement.classList.toggle('ss-has-size-chart', has);
      document.querySelectorAll('.ss-size-guide__link').forEach((a) => {
        if (has) a.setAttribute('aria-haspopup', 'dialog');
        else a.removeAttribute('aria-haspopup');
      });
      return has;
    };
    if (mark()) return;
    const mo = new MutationObserver(() => { if (mark()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 20000);
  }

  /* Stash (9 Oct 2026): add-to-bag now opens the cart drawer (Fabric cart_type drawer + auto-open); the old redirect to /cart was removed. */

  const start = () => {
    initSticky();
    initStickyBar();
    initStickyForward();
    initBreadcrumb();
    initSizeGuide();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
